import { useCallback, useEffect, useReducer, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BellOff, CheckCircle2, Link as LinkIcon, LogOut, MapPin, Phone, Timer } from 'lucide-react';
import { textIn, type AnswerError, type Answers } from '@rx-manager/forms';
import type { DraftAnswers } from '@rx-manager/forms/react';
import {
  zonedToday,
  type PublicBookingForm,
  type WaitlistOfferStatus,
  type WaitlistOfferView,
} from '@rx-manager/shared';
import { BookingApiError, bookingApi, waitlistApi } from '../../lib/api';
import { useI18n, type MessageKey } from '../../lib/i18n';
import { Alert, Button, Card, HEADING, IconTile, Spinner, buttonClasses } from '../../components/ui';
import { AppointmentHead, Detail } from '../../components/AppointmentCard';
import { useShellBrand } from '../../components/Shell';
import { Confirmation, ConsentBox } from '../BookingFlow';
import { FormsSummary } from '../booking/FormsSummary';
import { QuestionsStep } from '../booking/QuestionsStep';
import {
  NO_FORMS,
  allSettled,
  confirmForms,
  firstUnsettled,
  formLang,
  formsReducer,
  hasForms,
  type BookingFormsState,
} from '../booking/booking-forms';
import { hashOf, useHoldCountdown } from '../booking/shared';

/**
 * Track BCW (BCW-D22 to D24) — a time offered from the waitlist (`/o/<token>`). A held offer is
 * the patient's until `expiresAt`; a last-minute one went to a few people, and the first to book
 * gets it. The page asks the service's forms (the booking's runner, one question at a time) and
 * its consent, then books in one request. "No thanks" releases the time to the next person;
 * "Leave the waitlist" also ends the place.
 *
 * Nothing about the patient is shown or sent back: whoever holds the link reads the same page.
 * Answers live in memory only (SFM-D35).
 */

type Failure = { key: MessageKey } | { text: string };

const CLOSED_TEXT: Record<Exclude<WaitlistOfferStatus, 'open'>, MessageKey> = {
  booked: 'offer.closed.booked',
  declined: 'offer.closed.declined',
  lapsed: 'offer.closed.lapsed',
  taken: 'offer.closed.taken',
  withdrawn: 'offer.closed.withdrawn',
};

const OFFER_STATUSES = new Set<string>(['open', 'booked', 'declined', 'lapsed', 'taken', 'withdrawn']);

export function OfferPage() {
  const { token = '' } = useParams();
  const { t, locale } = useI18n();
  const [view, setView] = useState<WaitlistOfferView | null>(null);
  const [load, setLoad] = useState<'loading' | 'ready' | 'missing' | 'error'>('loading');
  const [forms, dispatch] = useReducer(formsReducer, NO_FORMS);
  const [stage, setStage] = useState<'offer' | 'questions'>('offer');
  const [consent, setConsent] = useState(false);
  const [confirmingLeave, setConfirmingLeave] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);
  /** What this visit did: says "still on the waitlist" or "left" only when it is true now. */
  const [outcome, setOutcome] = useState<'declined' | 'left' | 'taken' | null>(null);

  const msLeft = useHoldCountdown(view?.status === 'open' ? view.expiresAt : null);
  // The band names the pharmacy once the link resolves.
  useShellBrand(view ? { name: view.pharmacy.name, phone: view.pharmacy.phone } : null);

  const loadForms = useCallback(async (offer: WaitlistOfferView): Promise<PublicBookingForm[] | null> => {
    if (!offer.pharmacy.slug) return null;
    dispatch({ type: 'load_start', serviceId: offer.serviceId });
    try {
      const list = await bookingApi.serviceForms(offer.pharmacy.slug, offer.serviceId);
      dispatch({ type: 'loaded', serviceId: offer.serviceId, forms: list });
      return list;
    } catch {
      dispatch({ type: 'load_failed', serviceId: offer.serviceId });
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void waitlistApi
      .offer(token)
      .then((result) => {
        if (cancelled) return;
        setView(result);
        setLoad('ready');
        if (result.status === 'open' && result.hasForms) void loadForms(result);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setLoad(error instanceof BookingApiError && error.isMissing ? 'missing' : 'error');
      });
    return () => {
      cancelled = true;
    };
  }, [token, loadForms]);

  if (load === 'loading') return <Spinner label={t('time.loading')} />;
  if (load === 'missing') {
    return (
      <div className="flex flex-col items-center gap-3 py-6 text-center">
        <IconTile icon={LinkIcon} tone="muted" size="lg" />
        <p className="max-w-sm text-base text-muted-foreground">{t('entry.missing')}</p>
      </div>
    );
  }
  if (load === 'error' || !view) return <Alert>{t('error.unknown')}</Alert>;

  // The hold ran out while the page was open: the server says the same on the next call.
  const status: WaitlistOfferStatus = view.status === 'open' && msLeft === 0 ? 'lapsed' : view.status;
  const intl = locale === 'fr' ? 'fr-CA' : 'en-CA';
  const when = new Intl.DateTimeFormat(intl, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: view.timezone,
  }).format(new Date(view.startAt));
  // The 24-hour clock, as in the offer's text and email ("held for you until 14:30"). English's
  // "2:30 p.m." would also end the sentence in a doubled full stop ("p.m..").
  const until = new Intl.DateTimeFormat(intl, {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: view.timezone,
  }).format(new Date(view.expiresAt));
  const phone = view.location.phone ?? view.pharmacy.phone;

  const formsNeeded = view.hasForms && !forms.unavailable;
  const formsReady = !formsNeeded || (forms.load === 'ready' && allSettled(forms));

  const leaveConfirm = (
    <Card tone="muted" className="space-y-4">
      <p className="text-base font-medium text-heading">{t('entry.leaveQuestion')}</p>
      <div className="flex flex-wrap gap-3">
        <Button variant="outline" onClick={() => setConfirmingLeave(false)} disabled={busy}>
          {t('entry.stay')}
        </Button>
        <Button variant="danger" onClick={() => void decline(true)} loading={busy}>
          {t('entry.leaveConfirm')}
        </Button>
      </div>
    </Card>
  );

  async function decline(leave: boolean): Promise<void> {
    setBusy(true);
    setFailure(null);
    try {
      setView(await waitlistApi.decline(token, leave));
      setOutcome(leave ? 'left' : 'declined');
      setConfirmingLeave(false);
      setStage('offer');
    } catch (error) {
      setFailure(messageOf(error));
    } finally {
      setBusy(false);
    }
  }

  async function accept(): Promise<void> {
    if (!view) return;
    if (view.consentText && !consent) {
      setFailure({ key: 'offer.consentNeeded' });
      return;
    }
    setBusy(true);
    setFailure(null);
    try {
      setView(
        await waitlistApi.accept(token, {
          forms: confirmForms(forms, locale),
          consentGiven: consent,
          consentTextVersion: view.consentText ? hashOf(view.consentText) : undefined,
        }),
      );
    } catch (error) {
      if (error instanceof BookingApiError && error.code === 'offer_closed') {
        const ended = error.extra.status;
        const next = (typeof ended === 'string' && OFFER_STATUSES.has(ended) ? ended : 'lapsed') as WaitlistOfferStatus;
        setView({ ...view, status: next });
        if (next === 'taken') setOutcome('taken');
        return;
      }
      if (error instanceof BookingApiError && (await formRefusal(view, error))) return;
      setFailure(messageOf(error));
    } finally {
      setBusy(false);
    }
  }

  /** The server refused the forms and booked nothing (SFM-D31): back to the form it names. */
  async function formRefusal(offer: WaitlistOfferView, error: BookingApiError): Promise<boolean> {
    const formId = typeof error.extra.formId === 'string' ? error.extra.formId : null;
    const index = formId ? forms.entries.findIndex((entry) => entry.form.formId === formId) : -1;
    switch (error.code) {
      case 'form_answers_required': {
        const list = await loadForms(offer);
        const missing = Array.isArray(error.extra.formIds) ? (error.extra.formIds as string[]) : [];
        const at = (list ?? []).findIndex((form) => missing.includes(form.formId));
        if (at >= 0) {
          dispatch({ type: 'open', index: at });
          setStage('questions');
        }
        setFailure({ key: 'review.formsChanged' });
        return true;
      }
      case 'invalid_answers': {
        const errors = (error.extra.errors ?? {}) as Record<string, AnswerError>;
        if (index < 0 || Object.keys(errors).every((key) => key === '_form')) {
          await loadForms(offer);
          setFailure({ key: 'review.formsChanged' });
          return true;
        }
        dispatch({ type: 'open', index, view: { kind: 'runner', mount: 0, startAtReview: true, errors } });
        setStage('questions');
        return true;
      }
      case 'form_stopped': {
        if (index < 0) return false;
        const questionId = typeof error.extra.questionId === 'string' ? error.extra.questionId : undefined;
        dispatch({ type: 'open', index, view: { kind: 'runner', mount: 0, startAt: questionId } });
        setStage('questions');
        return true;
      }
      case 'forms_unavailable':
        // Book it again goes ahead without them; the pharmacy sends them later.
        dispatch({ type: 'unavailable' });
        setFailure({ key: 'review.formsUnavailable' });
        return true;
      default:
        return false;
    }
  }

  /** After a form: the next one still to answer, or back to the offer to book. */
  const afterForm = (after: BookingFormsState): void => {
    const next = firstUnsettled(after);
    if (next >= 0) {
      dispatch({ type: 'open', index: next });
      return;
    }
    setStage('offer');
  };

  if (stage === 'questions' && status === 'open') {
    const entry = forms.entries[forms.cursor];
    return (
      <div className="space-y-5">
        {entry && (
          <h1 tabIndex={-1} className={HEADING}>
            {textIn(entry.form.schema.title, formLang(entry.form, locale))}
          </h1>
        )}
        {failure && <Alert>{'key' in failure ? t(failure.key) : failure.text}</Alert>}
        <QuestionsStep
          state={forms}
          pageLang={locale}
          today={zonedToday(view.timezone)}
          phone={phone}
          notice={<LastMinute msLeft={msLeft} />}
          onAnswers={(answers: DraftAnswers) => dispatch({ type: 'answers', answers })}
          onFinish={(checked: Answers) => {
            const after = formsReducer(forms, { type: 'finish', checked });
            dispatch({ type: 'finish', checked });
            afterForm(after);
            return Promise.resolve();
          }}
          onSkip={() => {
            const after = formsReducer(forms, { type: 'skip' });
            dispatch({ type: 'skip' });
            afterForm(after);
          }}
          onAnswerNow={() => dispatch({ type: 'open', index: forms.cursor, view: { kind: 'runner', mount: 0 } })}
          onBack={() => {
            setFailure(null);
            const current = forms.entries[forms.cursor];
            if (forms.view.kind === 'runner' && current && !current.form.required) {
              dispatch({ type: 'open', index: forms.cursor, view: { kind: 'intro' } });
            } else if (forms.cursor > 0) {
              dispatch({ type: 'open', index: forms.cursor - 1 });
            } else {
              setStage('offer');
            }
          }}
          onLeave={() => setStage('offer')}
          leaveLabel={t('offer.backToOffer')}
        />
      </div>
    );
  }

  if (status === 'booked' && view.booking) {
    return (
      <Confirmation
        booking={view.booking}
        locale={locale}
        context={
          view.pharmacy.slug
            ? { slug: view.pharmacy.slug, serviceId: view.serviceId, locationId: view.locationId }
            : undefined
        }
      />
    );
  }

  const heading: MessageKey =
    status !== 'open' ? 'offer.heading.closed' : view.mode === 'held' ? 'offer.heading.held' : 'offer.heading.group';

  return (
    <div className="space-y-6">
      <h1 tabIndex={-1} className={HEADING}>
        {t(heading)}
      </h1>
      {failure && <Alert>{'key' in failure ? t(failure.key) : failure.text}</Alert>}

      {status === 'open' && (
        <p className="flex items-start gap-3 rounded-xl border border-primary/25 bg-accent p-4 text-base font-medium text-heading">
          <Timer className="mt-0.5 h-5 w-5 shrink-0 text-primary-strong" aria-hidden="true" />
          {view.mode === 'held' ? t('offer.heldUntil', { until }) : t('offer.groupLead', { until })}
        </p>
      )}
      {status === 'open' && <LastMinute msLeft={msLeft} />}

      {status !== 'open' && (
        <Card tone="muted" className="space-y-3">
          <p role="status" className="flex items-start gap-3 text-base font-medium text-heading">
            {status === 'booked' ? (
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" aria-hidden="true" />
            ) : (
              <BellOff className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
            )}
            {outcome === 'left' ? t('offer.left') : t(CLOSED_TEXT[status])}
            {(outcome === 'declined' || outcome === 'taken') && <> {t('offer.stillOn')}</>}
          </p>
          {phone && status !== 'booked' && (
            <a href={`tel:${phone}`} className={buttonClasses('outline', 'sm')}>
              <Phone className="h-4 w-4" aria-hidden="true" />
              {t('walkin.call', { phone })}
            </a>
          )}
        </Card>
      )}

      <div className="overflow-hidden rounded-xl border border-border">
        <AppointmentHead
          iso={view.startAt}
          timeZone={view.timezone}
          first={{ label: t('done.what'), value: view.serviceTitle }}
          second={{ label: t('done.when'), value: when }}
        />
        <dl className="px-4 sm:px-5">
          <Detail
            icon={MapPin}
            label={t('done.where')}
            value={[view.location.name, view.location.address].filter(Boolean).join(' · ')}
          />
        </dl>
      </div>

      {status === 'open' && (
        <>
          {formsNeeded && !view.pharmacy.slug && <Alert>{t('offer.formsCall')}</Alert>}
          {formsNeeded && forms.load === 'failed' && (
            <Alert>
              {t('offer.formsFailed')}{' '}
              <Button variant="outline" onClick={() => void loadForms(view)}>
                {t('offer.retry')}
              </Button>
            </Alert>
          )}
          {formsNeeded && !formsReady && forms.load === 'ready' && (
            <Alert tone="info">{t('offer.questionsFirst')}</Alert>
          )}
          {formsNeeded && formsReady && hasForms(forms) && (
            <FormsSummary
              entries={forms.entries}
              pageLang={locale}
              pharmacy={view.pharmacy.name}
              onChange={(index) => {
                dispatch({ type: 'open', index });
                setStage('questions');
              }}
            />
          )}

          {formsReady && view.consentText && (
            <section aria-labelledby="consent-heading" className="space-y-3 border-t border-border pt-6">
              <h2 id="consent-heading" className="text-lg font-semibold text-heading">
                {t('consent.heading')}
              </h2>
              {/* The pharmacist's own words, shown as plain text — never as markup. */}
              <p className="whitespace-pre-wrap rounded-xl bg-muted/70 p-4 text-base text-foreground">
                {view.consentText}
              </p>
              <ConsentBox checked={consent} onChange={setConsent} />
            </section>
          )}

          {confirmingLeave ? (
            leaveConfirm
          ) : (
            <div className="flex flex-col-reverse gap-3 border-t border-border pt-6 sm:flex-row sm:justify-between">
              <div className="flex flex-col-reverse gap-3 sm:flex-row">
                <Button variant="outline" onClick={() => setConfirmingLeave(true)} disabled={busy}>
                  <LogOut className="h-4 w-4" aria-hidden="true" />
                  {t('entry.leave')}
                </Button>
                <Button variant="outline" onClick={() => void decline(false)} disabled={busy}>
                  {t('offer.decline')}
                </Button>
              </div>
              {formsReady ? (
                <Button onClick={() => void accept()} loading={busy}>
                  {t('offer.book')}
                </Button>
              ) : (
                <Button
                  onClick={() => {
                    setFailure(null);
                    dispatch({ type: 'open', index: Math.max(firstUnsettled(forms), 0) });
                    setStage('questions');
                  }}
                  loading={forms.load === 'loading' || forms.load === 'idle'}
                  disabled={forms.load !== 'ready'}
                >
                  {t('offer.answerQuestions')}
                </Button>
              )}
            </div>
          )}
        </>
      )}

      {/* Every offer page can end the place, unless it is already over. */}
      {status !== 'open' && status !== 'booked' && outcome !== 'left' && (
        confirmingLeave ? (
          leaveConfirm
        ) : (
          <Button variant="outline" onClick={() => setConfirmingLeave(true)}>
            <LogOut className="h-4 w-4" aria-hidden="true" />
            {t('entry.leave')}
          </Button>
        )
      )}
    </div>
  );
}

/** The hold's last minute, said once, politely; and that it has gone. */
function LastMinute({ msLeft }: { msLeft: number | null }) {
  const { t } = useI18n();
  if (msLeft === null || msLeft > 60_000 || msLeft === 0) return null;
  return (
    <p
      aria-live="polite"
      className="flex items-center gap-2.5 rounded-xl border border-warning/50 bg-warning/15 px-4 py-3 text-base font-medium text-heading"
    >
      <Timer className="h-5 w-5 shrink-0 text-warning-foreground" aria-hidden="true" />
      {t('offer.lastMinute')}
    </p>
  );
}

function messageOf(error: unknown): Failure {
  if (!(error instanceof BookingApiError)) return { key: 'error.unknown' };
  if (error.message === 'offline') return { key: 'error.offline' };
  if (error.message === 'unknown') return { key: 'error.unknown' };
  return { text: error.message };
}
